import { useEffect, useState } from "react";
import LocationMapField from "@/components/LocationMapField/LocationMapField";
import Modal from "@/components/Modal/Modal";
import { combineClasses } from "@/lib/utils";
import { applyToEvent } from "@/services/api/events";
import styles from "./ConfirmAvailabilityModal.module.scss";

export interface ConfirmAvailabilityEvent {
  id: number | string;
  title: string;
}

interface ConfirmAvailabilityModalProps {
  event: ConfirmAvailabilityEvent | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ConfirmAvailabilityModal = ({
  event,
  isOpen,
  onClose,
  onSuccess,
}: ConfirmAvailabilityModalProps) => {
  const [location, setLocation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLocation("");
      setSubmitError(null);
    }
  }, [isOpen, event?.id]);

  if (!event) {
    return null;
  }

  const handleClose = () => {
    setLocation("");
    setSubmitError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!location.trim()) {
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await applyToEvent(event.id, {
        preferredLocation: location.trim(),
        eventId: event.id,
      });
      onSuccess?.();
      handleClose();
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      bodyClassName={styles.modalBody}
      className={combineClasses(styles.modal, "confirm-availability-modal")}
      closeModal={handleClose}
      heading="Confirm Your Availability"
      modalOpen={isOpen}
    >
      <div className={styles.panel}>
        <p className={styles.description}>
          Share your location and let us know if you&apos;re available for this
          dinner.
        </p>

        <LocationMapField
          compact
          hideError
          id="confirm-availability-location"
          onChange={setLocation}
          placeholder="Select location from map"
          value={location}
        />

        <p className={styles.availabilityLabel}>
          Are you available for this dinner?
        </p>

        <div className={styles.actionPair}>
          <button
            className={styles.secondaryButton}
            disabled={isSubmitting}
            onClick={handleClose}
            type="button"
          >
            Not This Time
          </button>
          <button
            className={styles.primaryButton}
            disabled={!location.trim() || isSubmitting}
            onClick={handleConfirm}
            type="button"
          >
            {isSubmitting ? "Submitting..." : "Yes, I'm Available"}
          </button>
        </div>

        {submitError ? <p className={styles.errorText}>{submitError}</p> : null}
      </div>
    </Modal>
  );
};

export default ConfirmAvailabilityModal;
